import React, { useEffect, useState } from 'react';
import TopNavigation, {
  TopNavigationProps,
} from '@amzn/awsui-components-react/polaris/top-navigation';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useUser } from '../hooks/user';
import { useFeatures, featureIsEnabled } from '../hooks/features';

import { externalLinks } from '../data/externalLinks';
import { publishKatalMetric } from './katalAnalytics';
import { useSpaWrapper } from './SpaLink';
import { IsItDownFlashbar } from '../utils/isItDownBanners';
import { LanguagePickerPanel } from './localization/languagePickerPanel';
import { CHANGE_LANGUAGE_NAV_ITEM_ID, LOCALIZATION } from '../utils/constants';
import { useLocalizedText } from '../hooks/localizedText';

export function TopNav() {
  const spaWrapper = useSpaWrapper();
  const { t } = useLocalizedText(['translation']);
  const [searchParams] = useSearchParams();

  const { userId } = useUser();
  const features = useFeatures();
  const { pathname } = useLocation();
  const [languagePanelOpen, setLanguagePanelOpen] = useState(false);
  const localizationEnabled = featureIsEnabled(LOCALIZATION, features, searchParams);

  // Closes the panel when the user navigates, so it does not float over the next page.
  useEffect(() => {
    setLanguagePanelOpen(false);
  }, [pathname]);

  // Changing language is the only thing in this menu, so without it the alias is just a label.
  const userUtility: TopNavigationProps.Utility = localizationEnabled
    ? {
        type: 'menu-dropdown',
        text: userId + '@',
        ariaLabel: userId + '@',
        onItemClick: ({ detail }) => {
          publishKatalMetric('Click', 'nav_' + detail.id);
          if (detail.id === CHANGE_LANGUAGE_NAV_ITEM_ID) {
            setLanguagePanelOpen(true);
          }
        },
        items: [
          {
            id: CHANGE_LANGUAGE_NAV_ITEM_ID,
            text: t('topnav.changeLanguage'),
            iconName: 'globe',
          },
        ],
      }
    : {
        type: 'button',
        text: userId + '@',
        variant: 'link',
      };

  const defaultUtilites: TopNavigationProps.Utility[] = [
    {
      type: 'button',
      text: t('topnav.search'),
      variant: 'link',
      href: '/',
      onFollow: spaWrapper({
        onFollow: () => {
          publishKatalMetric('Click', 'nav_search');
        },
      }),
    },
    {
      type: 'button',
      text: t('topnav.myActivity'),
      variant: 'link',
      href: '/my-activity',
      ariaLabel: t('topnav.myActivity'),
      onFollow: (event) => {
        publishKatalMetric('Click', 'nav_myActivity');
      },
    },
    {
      type: 'menu-dropdown',
      iconName: 'support',
      text: t('topnav.help'),
      onItemFollow: ({ detail }) => {
        publishKatalMetric('Click', 'nav_' + detail.id);
      },
      items: [
        {
          text: t('topnav.resources'),
          items: [
            {
              id: 'communityAmbassadorDirectory',
              text: t('topnav.communityAmbassadorDirectory'),
              href: '/ambassadors/',
            },
            {
              id: 'aciWiki',
              text: t('footer.aciWiki'),
              href: externalLinks.support.aciWiki,
              external: true,
            },
            {
              id: 'planningPlaybooks',
              text: t('topnav.planningPlaybooks'),
              href: externalLinks.support.planningPlaybooks,
              external: true,
            },
            {
              id: 'faqs',
              text: t('topnav.faqs'),
              href: externalLinks.support.faqs,
              external: true,
            },
          ],
        },
        {
          text: t('topnav.support'),
          items: [
            {
              id: 'question',
              text: t('topnav.question'),
              href: externalLinks.support.question,
              external: true,
            },
            {
              id: 'featureRequest',
              text: t('topnav.featureRequest'),
              href: externalLinks.support.feature,
              external: true,
            },
            {
              id: 'ticket',
              text: t('topnav.ticket'),
              href: externalLinks.support.ticket,
              external: true,
            },
          ],
        },
      ],
    },
    userUtility,
  ];

  if (featureIsEnabled('Hiring', features, searchParams)) {
    const hiringOption: TopNavigationProps.Utility = {
      type: 'button',
      text: t('topnav.hiring'),
      variant: 'link',
      href: externalLinks.support.hiring,
      target: '_blank',
      iconName: 'external',
      //there is no good way to css select a specific button in the title of topnav
      //unless you use the aria label. And you don't think to hard about the meaning of the word "good"
      //https://issues.amazon.com/issues/cce-4026
      ariaLabel: t('topnav.hiring'),
      onFollow: (event) => {
        publishKatalMetric('Click', 'nav_wereHiring');
      },
    };
    defaultUtilites.unshift(hiringOption);
  }

  return (
    <>
      <IsItDownFlashbar />
      {/* Anchors the language panel, which is absolutely positioned so opening it floats over the
          page instead of pushing every route's content down. */}
      <div className="vc-top-nav">
        <TopNavigation
          identity={{
            href: '/',
            //annoyingly something in cloudscape fubars the onFollow event
            //and removes the href. TLDR: you can't use spaWrapper because it doesn't know where
            //to navigate because there is no event.detail.href
            onFollow: spaWrapper({ url: '/' }),
            logo: {
              src: '/red-velvet-logo.svg',
              alt: t('programName'),
            },
          }}
          utilities={defaultUtilites}
        />
        {localizationEnabled && languagePanelOpen && (
          <LanguagePickerPanel onDismiss={() => setLanguagePanelOpen(false)} />
        )}
      </div>
    </>
  );
}
